import * as React from 'react'

import Avatar, {
  AvatarSize,
} from '~/client/src/shared/components/Avatar/Avatar'
import type { IDrillDownFilterCategory } from '~/client/src/shared/components/DrillDownFilter/DrillDownFilter.types'
import { ArchiveIcon } from '~/client/src/shared/components/icons/ArchiveIcon'
import { CheckSolidGreyIcon } from '~/client/src/shared/components/icons/CheckSolidGreyIcon'
import { ImportantAtIcon } from '~/client/src/shared/components/icons/ImportantAtIcon'
import { ImportantBellIcon } from '~/client/src/shared/components/icons/ImportantBellIcon'
import { ImportantBicIcon } from '~/client/src/shared/components/icons/ImportantBicIcon'
import { UnreadIcon } from '~/client/src/shared/components/icons/UnreadIcon'
import { VisibilityOffIcon } from '~/client/src/shared/components/icons/VisibilityOffIcon'
import { VisibilityOnIcon } from '~/client/src/shared/components/icons/VisibilityOnIcon'
import SitemapAttributeTag from '~/client/src/shared/components/SitemapAttributeTag/SitemapAttributeTag'
import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import { FILTER_TYPE_ICON_MAP } from '~/client/src/shared/constants/FilterTypeMappings'
import { getFormsStatusAsTag } from '~/client/src/shared/constants/formStatusesTags'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import { FilterType } from '~/client/src/shared/enums/FilterType'
import {
  notificationSavedViewFilterTypeForFieldKey,
  prefixedNotificationFilterId,
  stripKnownNotificationFilterPrefix,
} from '~/client/src/shared/lists/notifications/notificationFilterVocabulary'
import {
  LEGACY_READ_STATUS,
  NOTIFICATION_FIELD,
  NOTIFICATION_STATUSES,
  NOTIFICATION_TYPE,
} from '~/client/src/shared/lists/notifications/notificationSchema'
import { NotificationsListStore } from '~/client/src/shared/lists/notifications/NotificationsList.store'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import User from '~/client/src/shared/models/User'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'

export interface IFilterItem {
  id: string
  name: string
}

interface INotificationFilterCategoryDeps {
  readonly listStore: NotificationsListStore
  readonly companiesStore?: CompaniesStore
  readonly permitTypesStore?: WorkflowTypesStore
  readonly projectMembersStore?: ProjectMembersStore
  readonly locationAttributesStore?: LocationAttributesStore
}

const NTYPE_ICON_MAP: Record<string, React.ReactNode> = {
  [prefixedNotificationFilterId(
    NOTIFICATION_FIELD.notificationType,
    NOTIFICATION_TYPE.mention,
  )]: <ImportantAtIcon size="sm" className="tw:shrink-0" />,
  [prefixedNotificationFilterId(
    NOTIFICATION_FIELD.notificationType,
    NOTIFICATION_TYPE.bic,
  )]: <ImportantBicIcon size="sm" className="tw:shrink-0" />,
  [prefixedNotificationFilterId(
    NOTIFICATION_FIELD.notificationType,
    NOTIFICATION_TYPE.siteComm,
  )]: <ImportantBellIcon size="sm" className="tw:shrink-0" />,
  [prefixedNotificationFilterId(
    NOTIFICATION_FIELD.notificationType,
    NOTIFICATION_TYPE.update,
  )]: <CheckSolidGreyIcon size="sm" className="tw:shrink-0" />,
}

const READSTATUS_ICON_MAP: Record<string, React.ReactNode> = {
  [prefixedNotificationFilterId(
    NOTIFICATION_FIELD.readStatus,
    LEGACY_READ_STATUS.read,
  )]: <VisibilityOnIcon size="sm" className="tw:shrink-0" />,
  [prefixedNotificationFilterId(
    NOTIFICATION_FIELD.readStatus,
    LEGACY_READ_STATUS.unread,
  )]: <VisibilityOffIcon size="sm" className="tw:shrink-0" />,
  [prefixedNotificationFilterId(
    NOTIFICATION_FIELD.readStatus,
    LEGACY_READ_STATUS.archived,
  )]: <ArchiveIcon size="sm" className="tw:shrink-0" />,
}

const notificationTypeFilterType = notificationSavedViewFilterTypeForFieldKey(
  NOTIFICATION_FIELD.notificationType,
)
const readStatusFilterType = notificationSavedViewFilterTypeForFieldKey(
  NOTIFICATION_FIELD.readStatus,
)
const workflowTypeFilterType = notificationSavedViewFilterTypeForFieldKey(
  NOTIFICATION_FIELD.workflowType,
)
const workflowStatusFilterType = notificationSavedViewFilterTypeForFieldKey(
  NOTIFICATION_FIELD.workflowStatus,
)
const companyFilterType = notificationSavedViewFilterTypeForFieldKey(
  NOTIFICATION_FIELD.company,
)
const locationFilterType = notificationSavedViewFilterTypeForFieldKey(
  NOTIFICATION_FIELD.location,
)
const equipmentFilterType = notificationSavedViewFilterTypeForFieldKey(
  NOTIFICATION_FIELD.equipment,
)
const editorFilterType = notificationSavedViewFilterTypeForFieldKey(
  NOTIFICATION_FIELD.editor,
)

function prefixedFilterItemId(
  key: Parameters<typeof prefixedNotificationFilterId>[0],
  value: string,
): string {
  return prefixedNotificationFilterId(key, value)
}

function stripFilterItemIdPrefix(key: string, value: string): string {
  return stripKnownNotificationFilterPrefix(value, key)
}

export function buildNotificationsListFilterCategories(
  deps: INotificationFilterCategoryDeps,
): IDrillDownFilterCategory<IFilterItem>[] {
  return [
    buildNotificationTypeCategory(),
    buildReadStatusCategory(),
    buildWorkflowTypeCategory(deps),
    buildStatusCategory(),
    buildCompanyCategory(deps),
    buildLocationCategory(deps),
    buildEquipmentCategory(deps),
    buildEditorCategory(deps),
  ].filter(category => category.items.length > 0)
}

function renderRow(icon: React.ReactNode, name: string): React.ReactNode {
  return (
    <div className="tw:flex tw:items-center tw:w-full">
      {icon}
      <span>{name}</span>
    </div>
  )
}

function buildNotificationTypeCategory(): IDrillDownFilterCategory<IFilterItem> {
  return {
    id: notificationTypeFilterType,
    label: 'Notification Type',
    icon: <CheckSolidGreyIcon size="sm" className="tw:shrink-0" />,
    items: [
      {
        id: prefixedFilterItemId(
          NOTIFICATION_FIELD.notificationType,
          NOTIFICATION_TYPE.mention,
        ),
        name: Localization.translator.mention,
      },
      {
        id: prefixedFilterItemId(
          NOTIFICATION_FIELD.notificationType,
          NOTIFICATION_TYPE.bic,
        ),
        name: 'Ball in Court',
      },
      {
        id: prefixedFilterItemId(
          NOTIFICATION_FIELD.notificationType,
          NOTIFICATION_TYPE.siteComm,
        ),
        name: Localization.translator.siteCommunication,
      },
      {
        id: prefixedFilterItemId(
          NOTIFICATION_FIELD.notificationType,
          NOTIFICATION_TYPE.update,
        ),
        name: Localization.translator.updates,
      },
    ],
    getItemId: item => item.id,
    getItemLabel: item => item.name,
    renderItem: item => renderRow(NTYPE_ICON_MAP[item.id], item.name),
  }
}

function buildReadStatusCategory(): IDrillDownFilterCategory<IFilterItem> {
  return {
    id: readStatusFilterType,
    label: Localization.translator.readStatus || 'Read Status',
    icon: <UnreadIcon size="sm" className="tw:shrink-0" />,
    items: [
      {
        id: prefixedFilterItemId(
          NOTIFICATION_FIELD.readStatus,
          LEGACY_READ_STATUS.read,
        ),
        name: Localization.translator.read_2form,
      },
      {
        id: prefixedFilterItemId(
          NOTIFICATION_FIELD.readStatus,
          LEGACY_READ_STATUS.unread,
        ),
        name: Localization.translator.unread_2form,
      },
      {
        id: prefixedFilterItemId(
          NOTIFICATION_FIELD.readStatus,
          LEGACY_READ_STATUS.archived,
        ),
        name: Localization.translator.archived,
      },
    ],
    getItemId: item => item.id,
    getItemLabel: item => item.name,
    renderItem: item => renderRow(READSTATUS_ICON_MAP[item.id], item.name),
  }
}

function buildWorkflowTypeCategory({
  permitTypesStore,
}: INotificationFilterCategoryDeps): IDrillDownFilterCategory<IFilterItem> {
  const permitTypes = permitTypesStore?.actualTypes || []
  const items = permitTypes.map(type => ({
    id: prefixedFilterItemId(NOTIFICATION_FIELD.workflowType, type.type),
    name: type.name,
  }))

  return {
    id: workflowTypeFilterType,
    label: Localization.translator.workflowType || 'Workflow Type',
    icon: FILTER_TYPE_ICON_MAP[FilterType.Type],
    items,
    getItemId: item => item.id,
    getItemLabel: item => item.name,
    renderItem: item => {
      const typeKey = stripFilterItemIdPrefix(workflowTypeFilterType, item.id)
      const permitType = permitTypes.find(type => type.type === typeKey)
      return renderRow(
        <WorkflowTypeIcon
          iconName={permitType?.iconName || 'general'}
          size="medium"
          className="tw:shrink-0 tw:[&_svg]:w-5 tw:[&_svg]:h-5"
        />,
        item.name,
      )
    },
  }
}

function buildStatusCategory(): IDrillDownFilterCategory<IFilterItem> {
  const items = NOTIFICATION_STATUSES.map(status => ({
    id: prefixedFilterItemId(NOTIFICATION_FIELD.workflowStatus, status.id),
    name: status.label,
  }))

  return {
    id: workflowStatusFilterType,
    label: Localization.translator.workflowStatus || 'Workflow Status',
    icon: FILTER_TYPE_ICON_MAP[FilterType.Status],
    items,
    getItemId: item => item.id,
    getItemLabel: item => item.name,
    renderItem: item => {
      const statusId = stripFilterItemIdPrefix(
        workflowStatusFilterType,
        item.id,
      )
      const permitStatus = NOTIFICATION_STATUSES.find(
        status => status.id === statusId,
      )?.permitStatus
      if (permitStatus === undefined) return renderRow(null, item.name)
      const tag = getFormsStatusAsTag(permitStatus)
      return (
        <div className="tw:flex tw:items-center tw:w-full">
          <SitemapAttributeTag
            dataObject={
              {
                id: tag.id,
                name: tag.name,
                color: tag.color,
                iconName: tag.iconName,
                type: tag.type,
              } as LocationBase
            }
            shouldShowAsTag
            shouldHideIcon
            className="tw:flex tw:items-center"
          >
            <span>{tag.name}</span>
          </SitemapAttributeTag>
        </div>
      )
    },
  }
}

function buildCompanyCategory({
  companiesStore,
}: INotificationFilterCategoryDeps): IDrillDownFilterCategory<IFilterItem> {
  const companies = companiesStore?.allCompanies || []

  return {
    id: companyFilterType,
    label: Localization.translator.company,
    icon: FILTER_TYPE_ICON_MAP[FilterType.Company],
    items: companies.map(company => ({
      id: prefixedFilterItemId(NOTIFICATION_FIELD.company, company.id),
      name: company.name,
    })),
    getItemId: item => item.id,
    getItemLabel: item => item.name,
    renderItem: item => {
      const companyId = stripFilterItemIdPrefix(companyFilterType, item.id)
      const company = companiesStore?.getCompanyById(companyId)
      return renderRow(
        company?.avatarUrl || company?.logoUrl ? (
          <Avatar
            src={company.avatarUrl || company.logoUrl}
            size={AvatarSize.XMinuscule}
            avatarType={AvatarType.Company}
            className="tw:shrink-0"
          />
        ) : null,
        item.name,
      )
    },
  }
}

function buildLocationCategory({
  locationAttributesStore,
}: INotificationFilterCategoryDeps): IDrillDownFilterCategory<IFilterItem> {
  const locations = locationAttributesStore?.allLocationsWithoutEquipment || []
  const locById = new Map(locations.map(location => [location.id, location]))

  return {
    id: locationFilterType,
    label: Localization.translator.location,
    icon: FILTER_TYPE_ICON_MAP[FilterType.Location],
    items: locations.map(location => ({
      id: prefixedFilterItemId(NOTIFICATION_FIELD.location, location.id),
      name: location.name,
    })),
    getItemId: item => item.id,
    getItemLabel: item => item.name,
    renderItem: item => {
      const locationId = stripFilterItemIdPrefix(locationFilterType, item.id)
      const location = locById.get(locationId)
      if (!location) return renderRow(null, item.name)
      return (
        <div className="tw:flex tw:items-center tw:w-full">
          <SitemapAttributeTag
            dataObject={location}
            shouldShowAsTag={false}
            shouldHideIcon={false}
            className="tw:flex tw:items-center"
            iconSize={16}
          >
            <span>{location.name}</span>
          </SitemapAttributeTag>
        </div>
      )
    },
  }
}

function buildEquipmentCategory({
  locationAttributesStore,
}: INotificationFilterCategoryDeps): IDrillDownFilterCategory<IFilterItem> {
  const equipment =
    locationAttributesStore?.offloadingEquipmentsStore?.list || []
  const equipmentById = new Map(equipment.map(item => [item.id, item]))

  return {
    id: equipmentFilterType,
    label: Localization.translator.equipment || 'Equipment',
    icon: FILTER_TYPE_ICON_MAP[FilterType.Equipment],
    items: equipment.map(item => ({
      id: prefixedFilterItemId(NOTIFICATION_FIELD.equipment, item.id),
      name: item.name,
    })),
    getItemId: item => item.id,
    getItemLabel: item => item.name,
    renderItem: item => {
      const equipmentId = stripFilterItemIdPrefix(equipmentFilterType, item.id)
      const equipmentItem = equipmentById.get(equipmentId)
      if (!equipmentItem) return renderRow(null, item.name)
      return (
        <div className="tw:flex tw:items-center tw:w-full">
          <SitemapAttributeTag
            dataObject={equipmentItem}
            shouldShowAsTag={false}
            shouldHideIcon={false}
            className="tw:flex tw:items-center"
            iconSize={16}
          >
            <span>{equipmentItem.name}</span>
          </SitemapAttributeTag>
        </div>
      )
    },
  }
}

function buildEditorCategory({
  listStore,
  projectMembersStore,
}: INotificationFilterCategoryDeps): IDrillDownFilterCategory<IFilterItem> {
  const authorIds = new Set<string>()
  for (const row of listStore.list.inputRecords) {
    if (row.notification.authorId) authorIds.add(row.notification.authorId)
  }

  const authors = Array.from(authorIds).map(id => {
    const user = projectMembersStore?.getById(id)
    return { id, name: user ? User.getFullName(user) : id }
  })

  return {
    id: editorFilterType,
    label: 'Editor',
    icon: FILTER_TYPE_ICON_MAP[FilterType.ResponsibleContact],
    items: authors.map(author => ({
      id: prefixedFilterItemId(NOTIFICATION_FIELD.editor, author.id),
      name: author.name,
    })),
    getItemId: item => item.id,
    getItemLabel: item => item.name,
    renderItem: item => {
      const userId = stripFilterItemIdPrefix(editorFilterType, item.id)
      const user = projectMembersStore?.getById(userId)
      const initials = user ? User.getInitialsToDisplay(user) : ''
      return renderRow(
        <Avatar
          src={user?.avatarUrl}
          initials={initials}
          size={AvatarSize.XMinuscule}
          avatarType={AvatarType.User}
          className="tw:shrink-0"
        />,
        item.name,
      )
    },
  }
}
